// src/data/servicesData.ts
import { 
  FileText, Mic, Calculator, TrendingUp, Route, FileCheck, Award, Scale,
  Shield, Users, Crown, Rocket, UserPlus, PiggyBank, Heart, UserMinus, Globe,
  Brain, MessageSquare, FolderOpen, TrendingUp as TrendingUpAlt, Settings
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface ServiceItem {
  title: string;
  description: string[];
  icon: LucideIcon;
}

interface ServicesData {
  particuliers: ServiceItem[];
  professionnels: ServiceItem[];
  formations: ServiceItem[];
}

export const servicesData: ServicesData = {
  particuliers: [
    {
      title: 'Optimisation du CV et lettre de motivation',
      icon: FileText,
      description: [
        'Rédaction ou amélioration du CV pour le rendre clair et attractif.',
        'Adaptation de la lettre de motivation aux attentes des recruteurs.',
        'Optimisation du profil LinkedIn pour une visibilité accrue.',
        'Conseils personnalisés selon le secteur et le poste visé.'
      ]
    },
    {
      title: 'Formation à la prise de parole',
      icon: Mic,
      description: [
        'Techniques pour gérer le stress et parler avec confiance.',
        'Structuration du discours pour être clair et percutant.',
        'Entraînement aux entretiens d\'embauche et aux présentations professionnelles.',
        'Travail sur la voix et la posture pour améliorer l\'impact.'
      ]
    },
    {
      title: 'Aide à la compréhension du bulletin de salaire',
      icon: Calculator,
      description: [
        'Explication détaillée des cotisations et prélèvements.',
        'Analyse des erreurs fréquentes et conseils pour les corriger.',
        'Compréhension des droits liés au salaire (primes, heures supplémentaires, congés payés…).',
        'Ateliers individuels ou collectifs pour mieux gérer son bulletin de paie.'
      ]
    },
    {
      title: 'Coaching pour négociations salariales',
      icon: TrendingUp,
      description: [
        'Analyse du marché et des arguments pour justifier la demande.',
        'Préparation aux entretiens avec des simulations.',
        'Stratégies pour aborder la négociation avec confiance.',
        'Conseils pour maximiser ses chances d\'obtenir une revalorisation.'
      ]
    },
    {
      title: 'Accompagnement à la reconversion professionnelle',
      icon: Route,
      description: [
        'Identification des compétences transférables et des aspirations.',
        'Exploration des opportunités de formation et des financements possibles.',
        'Construction d\'un plan d\'action concret pour réussir la transition.',
        'Préparation aux entretiens et ajustement du CV pour le nouveau domaine.'
      ]
    },
    {
      title: 'Appui dans les démarches administratives',
      icon: FileCheck,
      description: [
        'Aide à la rédaction de courriers (demande de congé, rupture conventionnelle, réclamation…).',
        'Explication des droits du travail et des démarches à suivre.',
        'Accompagnement pour constituer un dossier en cas de litige avec l\'employeur.',
        'Orientation vers les bons interlocuteurs (avocats, inspection du travail, Prud\'hommes).'
      ]
    },
    {
      title: 'Diagnostic de compétences',
      icon: Award,
      description: [
        'Identification des compétences clés et des talents naturels.',
        'Évaluation des écarts avec le poste ou le secteur visé.',
        'Conseils pour valoriser ses compétences sur le CV et en entretien.',
        'Plan d\'action personnalisé pour développer ses atouts.'
      ]
    },
    {
      title: 'Formation en droit du travail pour salariés',
      icon: Scale,
      description: [
        'Explication des bases du droit du travail (contrat, licenciement, congés, heures supp\'…).',
        'Formation sur les recours possibles en cas de litige.',
        'Sensibilisation aux évolutions légales impactant les salariés.',
        'Ateliers pratiques pour apprendre à défendre ses droits.'
      ]
    }
  ],

  professionnels: [
    {
      title: 'Audit et conformité RH',
      icon: Shield,
      description: [
        'Vérification des contrats de travail et des obligations légales.',
        'Analyse des processus internes (gestion des congés, temps de travail, disciplinaire…).',
        'Recommandations sur les mises en conformité et amélioration des pratiques RH.'
      ]
    },
    {
      title: 'Accompagnement en gestion des conflits',
      icon: Users,
      description: [
        'Analyse de la situation et des enjeux humains et juridiques.',
        'Mise en place de solutions de dialogue et de médiation.',
        'Accompagnement des dirigeants et RH dans la gestion de conflits internes.',
        'Formation aux bonnes pratiques de gestion des tensions en entreprise.'
      ]
    },
    {
      title: 'Formation des managers',
      icon: Crown,
      description: [
        'Formation sur le management positif et la motivation des collaborateurs.',
        'Apprentissage des techniques de communication et gestion de conflits.',
        'Accompagnement à la délégation et au développement des talents.',
        'Sensibilisation aux obligations légales et aux bonnes pratiques RH.'
      ]
    },
    {
      title: 'Accompagnement à la création d\'entreprise',
      icon: Rocket,
      description: [
        'Conseil sur les obligations légales liées à l\'embauche et aux statuts juridiques.',
        'Rédaction des premiers contrats de travail et mise en place des registres obligatoires.',
        'Aide à la mise en place des outils RH (logiciels, bulletins de paie, fiches de poste…).',
        'Conseil en organisation du travail et structuration des équipes.'
      ]
    },
    {
      title: 'Recrutement et sélection des talents',
      icon: UserPlus,
      description: [
        'Rédaction et diffusion de l\'annonce.',
        'Tri et présélection des CV selon les critères du poste.',
        'Organisation et réalisation des entretiens téléphoniques et/ou physiques.',
        'Conseil et accompagnement sur le choix final du candidat.'
      ]
    },
    {
      title: 'Optimisation des coûts RH',
      icon: PiggyBank,
      description: [
        'Étude des charges sociales et identification des optimisations possibles.',
        'Conseil sur les aides à l\'embauche et exonérations disponibles.',
        'Optimisation de la politique de rémunération (primes, avantages en nature, intéressement…).',
        'Stratégies pour réduire le coût du turnover et de l\'absentéisme.'
      ]
    },
    {
      title: 'Politique de qualité de vie au travail (QVT)',
      icon: Heart,
      description: [
        'Diagnostic des attentes des salariés en matière de bien-être au travail.',
        'Mise en place de mesures adaptées : télétravail, flexibilité, ergonomie des postes.',
        'Développement d\'initiatives bien-être : ateliers de sensibilisation, coaching, activités de cohésion.',
        'Suivi et évaluation de l\'impact des actions mises en place.'
      ]
    },
    {
      title: 'Gestion des départs et ruptures contractuelles',
      icon: UserMinus,
      description: [
        'Conseil sur les options possibles : rupture conventionnelle, licenciement, démission.',
        'Assistance dans la rédaction des documents et la gestion des formalités.',
        'Accompagnement pour limiter les risques de litiges et contentieux.',
        'Stratégies pour un offboarding réussi et préserver l\'image de l\'entreprise.'
      ]
    },
    {
      title: 'Mise en place d\'une politique de diversité et inclusion',
      icon: Globe,
      description: [
        'Diagnostic des pratiques en matière d\'égalité et de non-discrimination.',
        'Mise en place d\'actions concrètes pour favoriser l\'inclusion.',
        'Sensibilisation des équipes et formation aux biais inconscients.',
        'Accompagnement pour répondre aux obligations légales et aux labels diversité.'
      ]
    }
  ],

  formations: [
    {
      title: 'Formations en management et leadership',
      icon: Crown,
      description: [
        'Développement du leadership et de la posture managériale.',
        'Management d\'équipe au quotidien : fixer un cadre, motiver, responsabiliser.',
        'Communication managériale et feedback constructif.',
        'Prévention des tensions et gestion des situations délicates.'
      ]
    },
    {
      title: 'Gestion du stress et prévention des risques psychosociaux (RPS)',
      icon: Brain,
      description: [
        'Compréhension des mécanismes du stress au travail.',
        'Identification des signaux d\'alerte individuels et collectifs.',
        'Outils concrets de régulation du stress en situation professionnelle.',
        'Sensibilisation aux RPS et obligations de l\'employeur.'
      ]
    },
    {
      title: 'Communication professionnelle et relations de travail',
      icon: MessageSquare,
      description: [
        'Communication assertive et écoute active.',
        'Gestion des désaccords et des tensions au quotidien.',
        'Travail sur les comportements et les postures professionnelles.',
        'Amélioration de la coopération interservices.'
      ]
    },
    {
      title: 'Sensibilisation au droit du travail pour managers et dirigeants',
      icon: Scale,
      description: [
        'Rôles et responsabilités du manager.',
        'Gestion du temps de travail, absences et discipline.',
        'Bonnes pratiques en matière de sanctions et procédures.',
        'Prévention des risques juridiques et contentieux.'
      ]
    },
    {
      title: 'Formation à la gestion RH pour non-spécialistes',
      icon: FolderOpen,
      description: [
        'Fondamentaux RH : contrats, obligations, documents clés.',
        'Organisation RH simple et efficace.',
        'Lecture et compréhension des bulletins de paie.',
        'Anticipation des risques sociaux.'
      ]
    },
    {
      title: 'Formation à la prise de poste et à l\'évolution professionnelle',
      icon: TrendingUpAlt,
      description: [
        'Intégration et prise de poste réussie.',
        'Organisation du travail et priorisation.',
        'Développement de la confiance professionnelle.',
        'Préparation à une évolution ou une prise de responsabilité.'
      ]
    },
    {
      title: 'Formation à la prévention et gestion des conflits',
      icon: Shield,
      description: [
        'Compréhension des mécanismes du conflit.',
        'Techniques de désamorçage et médiation de premier niveau.',
        'Posture managériale face aux tensions.',
        'Construction de solutions durables.'
      ]
    },
    {
      title: 'Formations sur-mesure intra-entreprise',
      icon: Settings,
      description: [
        'Analyse des besoins et objectifs.',
        'Contenu personnalisé selon le public.',
        'Cas pratiques issus de l\'entreprise.',
        'Évaluation et ajustement post-formation.'
      ]
    }
  ]
};